import { PMT } from './utils.js';
import { createChart } from './charts.js';
import { applyAgentRowStyles, applyAgencyRowStyles } from './tables.js';

export function initAlpineData() {
    Alpine.data('mortgageCalculator', () => ({
        principalLoan: document.getElementById('principal_loan').value,
        term: document.getElementById('term').value,
        annualInterestRate: document.getElementById('annual_interest_rate').value,
        mpPrincipalInterest: document.getElementById('mp_principal_interest').value,
        mortgagePayment: document.getElementById('mortgage_payment').value,
        iulPremium: document.getElementById('iul_premium').value,
        costLifeInsurance: document.getElementById('cost_life_insurance').value,
        iulPremiumPercentage: parseFloat(document.getElementById('iul_premium_percentage').value) / 100,

        calculateMI() {
            const rate = parseFloat(this.annualInterestRate) / 100 / 12;
            const nper = parseInt(this.term) * 12;
            const pv = -parseFloat(this.principalLoan.replace(/,/g, ''));

            if (!isNaN(rate) && !isNaN(nper) && !isNaN(pv)) {
                this.mpPrincipalInterest = (PMT(rate, nper, pv)).toFixed(2);
            } else {
                this.mpPrincipalInterest = 0;
            }
        },

        calculateIUL() {
            const mortgage = parseFloat(this.mortgagePayment.replace(/,/g, ''));
            const multiple = 1.0 + this.iulPremiumPercentage;
            this.iulPremium = (mortgage * multiple).toFixed(2);
        },

        calculateCostLifeInsurance() {
            const mortgage = parseFloat(this.mortgagePayment.replace(/,/g, ''));
            const iulPremium = parseFloat(this.iulPremium.replace(/,/g, ''));
            this.costLifeInsurance = iulPremium * (this.iulPremiumPercentage).toFixed(2);
        }
    }));

    Alpine.data('comparisonData', (firstLienSelected = true, amortizationBalances = [], firstLienBalances = [], secondLienBalances = []) => ({
        firstLienSelected: firstLienSelected,
        amortizationBalances: amortizationBalances.map(item => parseFloat(item.beginning_balance.replace(/[^0-9.-]+/g,""))),
        firstLienBalances: firstLienBalances.map(item => parseFloat(item.beginning_balance.replace(/[^0-9.-]+/g,""))),
        secondLienBalances: secondLienBalances.map(item => parseFloat(item.debt_load.replace(/[^0-9.-]+/g,""))),
        labels: amortizationBalances.map(item => item.payment_num),

        updateChart() {
            if (this.firstLienSelected) {
                createChart(this.amortizationBalances, this.firstLienBalances, '1st Lien HELOC', this.labels);
            } else {
                createChart(this.amortizationBalances, this.secondLienBalances, '2nd Lien HELOC', this.labels);
            }
        },

        init() {
            this.updateChart();
        }
    }));

    Alpine.data('agentsTable', () => ({
        addAgentModalIsOpen: false,
        transferAgentModalIsOpen: false,
        selectedAgentId: null,
        dataTable: null,

        init() {
            this.initializeDataTable();

            const eventUpdates = {
                'agent-transferred': { column: 3, value: e => e.detail.agencyName },
                'agent-activate-toggled': {
                    column: 4,
                    value: e => e.detail.activeStatus,
                    callback: function(event, rowNode) {
                        // Get the last cell (actions column) from the row
                        const actionsCell = rowNode.lastElementChild;
                        const dropdown = actionsCell.querySelector('.actions-dropdown');
                        const toggleButton = dropdown.querySelector('[x-ref="toggleActiveButton"] span');
                        if (toggleButton) {
                            toggleButton.textContent = event.detail.buttonText;
                        }
                    }
                }
            };

            Object.entries(eventUpdates).forEach(([eventName, config]) => {
                window.addEventListener(eventName, (event) => {
                    if (this.dataTable) {
                        const row = this.dataTable.row(`tr[data-agent-id="${event.detail.agentId}"]`);
                        if (row) {
                            let new_row_data = row.data();
                            new_row_data[config.column] = config.value(event);
                            row.data(new_row_data);

                            applyAgentRowStyles(row.node(), new_row_data);
                            row.draw(false);

                            // Execute callback if it exists, passing the row node
                            if (config.callback) {
                                config.callback(event, row.node());
                            }

                        }
                    }
                });
            });

            // Listen for agent deleted event
            window.addEventListener('agent-deleted', (event) => {
                if (this.dataTable) {
                    const row = this.dataTable.row(`tr[data-agent-id="${event.detail.agentId}"]`);
                    if (row) {
                        row.remove().draw(false);
                    }
                }
            });


            // Listen for transfer modal open event
            window.addEventListener('open-transfer-modal', (event) => {
                this.selectedAgentId = event.detail.agentId;
                this.transferAgentModalIsOpen = true;
            });

            window.addEventListener('agent-created', (event) => {
                if (this.dataTable) {
                    const newAgent = event.detail.agent;
                    const isActive = event.detail.is_active;
                    const agencyName = event.detail.agency;

                    // Get actions HTML from first row of table
                    const firstRow = this.dataTable.row(0).node();
                    let actionsHtml = '';
                    if (firstRow) {
                        const actionsCell = firstRow.lastElementChild;
                        actionsHtml = actionsCell.innerHTML
                            // Replace agent.id in transfer modal dispatch
                            .replace(
                                /\$dispatch\('open-transfer-modal', \{ agentId: \d+ \}\)/,
                                `$dispatch('open-transfer-modal', { agentId: ${newAgent.id} })`
                            )
                            // Replace agent.id in toggle active endpoint
                            .replace(
                                /hx-vals="\{&quot;agent_id&quot;:\s*\d+\}"/,
                                `hx-vals="{&quot;agent_id&quot;: ${newAgent.id}}"`
                            )
                            // Replace agent.id in delete endpoint
                            .replace(
                                /hx-delete="\/api\/agents\/\d+"/,
                                `hx-delete="/api/agents/${newAgent.id}"`
                            )
                            // Replace the toggle active button text based on is_active status
                            .replace(
                                /<span>(?:Activate|Deactivate)<\/span>/,
                                `<span>${isActive ? 'Deactivate' : 'Activate'}</span>`
                            );
                    } else {
                        window.location.reload();
                        return;
                    }

                    // Create new row data array matching table structure
                    const rowData = [
                        newAgent.id,
                        newAgent.name,
                        newAgent.email,
                        agencyName,
                        isActive ? 'Active' : 'Inactive',
                        actionsHtml
                    ];

                    // Add new row and get the API result
                    const api = this.dataTable.row.add(rowData);

                    // Navigate to the first page and then draw
                    this.dataTable.page('first').draw(false);

                    setTimeout(() => {
                        const newRow = api.node();
                        if (newRow) {
                            newRow.setAttribute('data-agent-id', newAgent.id);
                            applyAgentRowStyles(newRow, rowData);
                        }
                    });
                }
            });
        },

        initializeDataTable() {
            this.dataTable = new DataTable('#agents-table');
        },

        refreshDataTable() {
            if (this.dataTable) {
                this.dataTable.draw(false);
            }
        }
    }));

    Alpine.data('agenciesTable', () => ({
        addAgencyModalIsOpen: false,
        dataTable: null,

        init() {
            this.initializeDataTable();

            const eventUpdates = {
                'agency-activate-toggled': {
                    column: 3,
                    value: e => e.detail.activeStatus,
                    callback: function(event, rowNode) {
                        const actionsCell = rowNode.lastElementChild;
                        const dropdown = actionsCell.querySelector('.actions-dropdown');
                        const toggleButton = dropdown.querySelector('a span');
                        if (toggleButton) {
                            toggleButton.textContent = event.detail.buttonText;
                        }
                    }
                },
            };

            Object.entries(eventUpdates).forEach(([eventName, config]) => {
                window.addEventListener(eventName, (event) => {
                    if (this.dataTable) {
                        const row = this.dataTable.row(`tr[data-agency-id="${event.detail.agencyId}"]`);
                        if (row) {
                            let new_row_data = row.data();
                            new_row_data[config.column] = config.value(event);
                            row.data(new_row_data);
                            applyAgencyRowStyles(row.node(), new_row_data);
                            row.draw(false);

                            if (config.callback) {
                                config.callback(event, row.node());
                            }
                        }
                    }
                });
            });

            // Listen for agency deleted event
            window.addEventListener('agency-deleted', (event) => {
                if (this.dataTable) {
                    const row = this.dataTable.row(`tr[data-agency-id="${event.detail.agencyId}"]`);
                    if (row) {
                        row.remove().draw(false);
                    }
                }
            });

            // Add new event listener for agency creation
            window.addEventListener('agency-created', (event) => {
                if (this.dataTable) {
                    const newAgency = event.detail;

                    // Get actions HTML from first row of table
                    const firstRow = this.dataTable.row(0).node();
                    let actionsHtml = '';
                    if (firstRow) {
                        const actionsCell = firstRow.lastElementChild;
                        actionsHtml = actionsCell.innerHTML
                            // Replace agency.id in toggle active endpoint
                            .replace(
                                /hx-vals="\{&quot;agency_id&quot;:\s*\d+\}"/,
                                `hx-vals="{&quot;agency_id&quot;: ${newAgency.id}}"`
                            )
                            // Replace agency.id in delete endpoint
                            .replace(
                                /hx-delete="\/api\/agencies\/\d+"/,
                                `hx-delete="/api/agencies/${newAgency.id}"`
                            )
                            // Replace the toggle active button text based on is_active status
                            .replace(
                                /<span>(?:Activate|Deactivate)<\/span>/,
                                `<span>${newAgency.is_active ? 'Deactivate' : 'Activate'}</span>`
                            );
                    } else {
                        window.location.reload();
                        return;
                    }

                    // Create new row data array matching table structure
                    const rowData = [
                        newAgency.id,
                        newAgency.name,
                        newAgency.email,
                        newAgency.is_active ? 'Active' : 'Inactive',
                        actionsHtml
                    ];

                    // Add new row and get the API result
                    const api = this.dataTable.row.add(rowData);

                    // Navigate to the first page and then draw
                    this.dataTable.page('first').draw(false);

                    setTimeout(() => {
                        // Now get the node after drawing
                        const newRow = api.node();

                        if (newRow) {
                            // Set the agency ID on the row
                            newRow.setAttribute('data-agency-id', newAgency.id);
                            // Apply styles to the new row
                            applyAgencyRowStyles(newRow, rowData);
                        }
                    });
                }
            });
        },

        initializeDataTable() {
            this.dataTable = new DataTable('#agencies-table');
        },

        refreshDataTable() {
            if (this.dataTable) {
                this.dataTable.draw(false);
            }
        }
    }));

    Alpine.data('clientsTable', () => ({
        dataTable: null,
        addClientModalIsOpen: false,
        transferClientModalIsOpen: false,
        selectedClientId: null,

        init() {
            this.initializeDataTable();

            // Listen for transfer modal open event
            window.addEventListener('open-transfer-modal', (event) => {
                this.selectedClientId = event.detail.clientId;
                this.transferClientModalIsOpen = true;
            });

            // Listen for client transferred event
            window.addEventListener('client-transferred', (event) => {
                if (this.dataTable) {
                    const row = this.dataTable.row(`tr[data-client-id="${event.detail.clientId}"]`);
                    if (row) {
                        let new_row_data = row.data();
                        new_row_data[3] = event.detail.agentId;
                        new_row_data[4] = event.detail.agentName;
                        row.data(new_row_data);
                        row.draw(false);
                    }
                }
            });

            // Listen for client deleted event
            window.addEventListener('client-deleted', (event) => {
                if (this.dataTable) {
                    const row = this.dataTable.row(`tr[data-client-id="${event.detail.clientId}"]`);
                    if (row) {
                        row.remove().draw(false);
                    }
                }
            });

            window.addEventListener('client-created', (event) => {
                if (this.dataTable) {
                    const newClient = event.detail.client;
                    const agentName = event.detail.agent;
                    const agentId = event.detail.agentId;

                    // Get actions HTML from first row of table
                    const firstRow = this.dataTable.row(0).node();
                    let actionsHtml = '';
                    if (firstRow) {
                        const actionsCell = firstRow.lastElementChild;
                        actionsHtml = actionsCell.innerHTML
                            // Replace client.id in overview URL
                            .replace(
                                /window\.location='\/clients\/\d+\/overview'/,
                                `window.location='/clients/${newClient.id}/overview'`
                            )
                            // Replace client.id in comparison URL
                            .replace(
                                /window\.location='\/clients\/\d+\/comparison'/,
                                `window.location='/clients/${newClient.id}/comparison'`
                            )
                            // Replace client.id in transfer modal dispatch
                            .replace(
                                /\$dispatch\('open-transfer-modal', \{ clientId: \d+ \}\)/,
                                `$dispatch('open-transfer-modal', { clientId: ${newClient.id} })`
                            )
                            // Replace client.id in delete endpoint
                            .replace(
                                /hx-delete="\/api\/clients\/\d+"/,
                                `hx-delete="/api/clients/${newClient.id}"`
                            );
                    } else {
                        window.location.reload();
                        return;
                    }

                    // Create new row data array matching table structure
                    const rowData = [
                        newClient.id,
                        newClient.name,
                        newClient.email,
                        agentId,
                        agentName,
                        actionsHtml
                    ];

                    // Add new row and get the API result
                    const api = this.dataTable.row.add(rowData);

                    // Navigate to the first page and then draw
                    this.dataTable.page('first').draw(false);

                    setTimeout(() => {
                        const newRow = api.node();
                        if (newRow) {
                            newRow.setAttribute('data-client-id', newClient.id);
                            // If you have client-specific styles, apply them here
                            // applyClientRowStyles(newRow, rowData);
                        }
                    });
                }
            });
        },

        initializeDataTable() {
            this.dataTable = new DataTable('#clients-table');
        },

        refreshDataTable() {
            if (this.dataTable) {
                this.dataTable.draw(false);
            }
        }
    }));
}
